import { useMemo } from 'react';

import { useFeatureFlags } from 'contexts/FeatureFlags';
import { useSPMCollaborationContext } from 'contexts/SPMCollaborationContext';
import { useSPMPlanContext } from 'contexts/SPMPlanContext';
import { canViewCollaboratorResource } from 'hooks/canViewCollaboratorResource';
import { UsersService } from 'services';
import { SPMScenario } from 'types/SPM';
import { TQModel } from 'types/tqModels';

export type NavigationData = {
  activeScenario: SPMScenario;
  tqModel: TQModel;
  canViewQuotaModel: boolean;
  canViewCapacityModel: boolean;
  canViewTerritoriesModel: boolean;
};

export function useNavigationData(): NavigationData {
  const { query: permissionsQuery } = UsersService.useGetCurrentUserPermissions();
  const userPermissions = permissionsQuery.data;
  const { isCollaborationUser } = useSPMCollaborationContext();
  const { isFeatureEnabled } = useFeatureFlags();
  const { planningCycle, activeScenario, canViewQuotaModel, canViewCapacityModel } = useSPMPlanContext();
  const isPermissionsV2Enabled = isFeatureEnabled('feature_flag_spm_permissions_v2');

  const canViewTerritoriesModel = useMemo(() => {
    if (!activeScenario) {
      return false;
    }
    return canViewCollaboratorResource({
      isCollaborationUser,
      userPermissions,
      planningCycleId: planningCycle.id,
      activeScenarioId: activeScenario.id,
      isPermissionsV2Enabled,
      modelId: activeScenario.hierarchy_model.id,
      modelType: 'territory',
    });
  }, [activeScenario, userPermissions, isCollaborationUser, planningCycle, isPermissionsV2Enabled]);

  return useMemo(() => {
    if (!activeScenario) {
      return {} as NavigationData;
    }
    return {
      activeScenario,
      tqModel: activeScenario.hierarchy_model,
      canViewQuotaModel,
      canViewCapacityModel,
      canViewTerritoriesModel,
    };
  }, [activeScenario, canViewQuotaModel, canViewCapacityModel, canViewTerritoriesModel]);
}
